import { Building2, Download, X } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { getUserManagementEditUrl } from '@/lib/utils/userManagementNavigation'
import type { UsePaymentsTabReturn } from '@/features/company/types'
import {
    FILE_TYPES,
    MAX_PROPERTIES_SCROLL,
    PAY_ADDR_INVALID_RING,
    normalizePaymentFiles,
    propertyPaymentRows,
    serverSaysUseCompanyPaymentForAllProperties,
    getPropertyPayeeStaffForDropdown,
    getPropertyRowAfterPayToChange,
    shouldShowPropertyAchRow,
    shouldShowPropertyPaperRow,
} from '@/features/company/utils/paymentsTabLogic'
import { formatPaymentUpdatedDate } from '@/features/company/utils/paymentDateFormat'
import { PAY_FIELD, PAY_BTN_PRIMARY, PAY_LINK, PROP_LABEL, PROP_SECTION_TITLE, PROP_INNER_CARD } from './paymentsTabStyles'
import { PaymentAddrFieldError } from './PaymentAddrFieldError'
import { AchPaymentFormHelperTip } from './AchPaymentFormHelperTip'
import { PaymentFileUploadField } from './PaymentFileUploadField'
import { SectionCard } from '@/components/ui/SectionCard'

export function PaymentsTabPropertySection({
        company,
        companyForm,
        states,
        canEdit,
        canDefaultPaymentLogs,
        propertyPaymentsLocked,
        showPropertyPaymentSection,
        propertyForms,
        displayedPropertyForms,
        propertyFormsScrollable,
        propertyJumpToValue,
        setPropertyJumpToValue,
        saving,
        uploading,
        filePickerHint,
        setFilePickerHint,
        paymentAddrValidateErrors,
        updateCompanyForm,
        updatePropertyForm,
        clearPaymentAddrFieldError,
        handleDownloadAchForm,
        handleDownloadFile,
        handleUploadFile,
        handleValidatePaymentAddress,
        setShowDeleteFileConfirm,
        setSavePropertyIndex,
        navigateToDefaultPaymentLogs,
}: UsePaymentsTabReturn) {
    return (
        <>
            {showPropertyPaymentSection ? (
            <SectionCard
                id="property-payment-info"
                icon={Building2}
                title="Property payment info"
                testId="company-payments-property-payment-section"
                className="scroll-mt-6"
                headerActions={
                    <>
                        <button
                            type="button"
                            data-testid="company-payments-property-section-download-ach-form-button"
                            onClick={handleDownloadAchForm}
                            className={cn(PAY_LINK, "inline-flex items-center gap-1.5")}
                        >
                            <Download size={16} strokeWidth={2} /> Download ACH Form
                        </button>
                        <div className="flex items-center gap-2">
                            <label
                                htmlFor="company-payments-jump-to-property-select"
                                id="company-payments-jump-to-label"
                                className="shrink-0 text-sm font-medium text-slate-700"
                            >
                                Jump To
                            </label>
                            <select
                                id="company-payments-jump-to-property-select"
                                aria-labelledby="company-payments-jump-to-label"
                                data-testid="company-payments-jump-to-property-select"
                                className={cn(
                                    PAY_FIELD,
                                    "h-10 min-h-10 w-60 min-w-[15rem] max-w-[18rem] py-2 leading-normal",
                                )}
                                value={propertyJumpToValue}
                                onChange={(e) => {
                                    const v = e.target.value
                                    setPropertyJumpToValue(v)
                                    if (v && v.startsWith("property_")) {
                                        const idx = v.replace("property_", "")
                                        document
                                            .getElementById(`property_${idx}`)
                                            ?.scrollIntoView({ behavior: "smooth", block: "nearest" })
                                    }
                                }}
                            >
                                <option value="">Select Property</option>
                                {propertyForms.map((p, i) => (
                                    <option key={p.property_id} value={`property_${i}`}>
                                        {p.property_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                }
            >
                {propertyForms.length > MAX_PROPERTIES_SCROLL && (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        Too many properties to display. Use Jump To to find a property.
                    </p>
                )}

                <div
                    className={cn(
                        propertyFormsScrollable &&
                            "relative max-h-[36rem] max-h-[70vh] min-h-0 shrink overflow-y-auto overflow-x-auto overscroll-y-contain rounded-xl border border-slate-200/80 bg-slate-50 p-3 shadow-inner [scrollbar-gutter:stable] sm:p-4",
                    )}
                    style={propertyFormsScrollable ? { maxHeight: "min(36rem, 70vh)" } : undefined}
                    data-testid={
                        propertyFormsScrollable
                            ? "company-payments-property-forms-scroll-region"
                            : undefined
                    }
                    aria-label={
                        propertyFormsScrollable ? "Property payment forms, scroll to view all" : undefined
                    }
                >
                    <div className="space-y-4">
                    {displayedPropertyForms.map((prop, index) => {
                        const showPropertyAchRow = shouldShowPropertyAchRow(prop, companyForm.payment_method)
                        const showPropertyPaperRow = shouldShowPropertyPaperRow(prop, companyForm.payment_method)
                        /** Sidebar (Pay To / Method / Verified): locked when company pays all properties (Angular outer wrapper). */
                        const propertySidebarDisabled = !canEdit || propertyPaymentsLocked
                        /**
                         * ACH / paper detail rows: Angular also sets `control-nonclickable` when
                         * `property_payment[i].pay_to === 'COMPANY'` (mirrors company payment from that row).
                         */
                        const propertyAchPaperDetailDisabled =
                            !canEdit || propertyPaymentsLocked || prop.pay_to === "COMPANY"
                        const useCompanyStaffForPayee =
                            prop.pay_to === "COMPANY" ||
                            serverSaysUseCompanyPaymentForAllProperties(company)
                        const staffForPayee = getPropertyPayeeStaffForDropdown(company, index, useCompanyStaffForPayee)
                        const pp = propertyPaymentRows(company)[index]
                        const payeeNameStr = String(prop.payee_name ?? "")
                        const showPropEmailBlock =
                            showPropertyPaperRow && !["LEASING", "OTHER"].includes(payeeNameStr)
                        const propEmailDisplay =
                            prop.pay_to === "PROPERTY"
                                ? String(pp?.["payee_email"] ?? "")
                                : String(company?.["payee_email"] ?? "")
                        const propVerifiedDisplay =
                            prop.pay_to === "PROPERTY"
                                ? pp?.["payee_email_verify"]
                                    ? String(pp["payee_email_verify"])
                                    : "Unverified"
                                : company?.["payee_email_verify"]
                                  ? String(company?.["payee_email_verify"])
                                  : "Unverified"
                        const showEditPropPayeeUser =
                            prop.pay_to === "PROPERTY" &&
                            !["LEASING", "OTHER"].includes(payeeNameStr) &&
                            pp != null &&
                            pp["payee_user_id"] != null &&
                            String(pp["payee_user_id"]) !== ""
                        return (
                                <div
                                    key={prop.property_id}
                                    id={`property_${index}`}
                                    data-testid={`company-payments-property-${prop.property_id}-card`}
                                    className={cn(
                                        "overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm transition",
                                        propertyFormsScrollable && "scroll-mt-4",
                                    )}
                                >
                                <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-0">
                                    <aside className="flex shrink-0 flex-col space-y-3 rounded-xl border border-slate-200/80 bg-slate-50/40 p-4 lg:w-56 lg:rounded-l-xl lg:rounded-r-none lg:border-r-0 lg:border-y lg:border-l lg:bg-slate-50/30 lg:p-5">
                                        <h3 className="border-b border-slate-200/80 pb-2.5 text-base font-semibold text-slate-900">
                                            <span className="mr-1.5 tabular-nums text-slate-500">{index + 1}.</span>
                                            {prop.property_name}
                                        </h3>
                                        <div>
                                            <label className={PROP_LABEL}>Pay To</label>
                                            <select
                                                data-testid={`company-payments-property-${prop.property_id}-pay-to-select`}
                                                value={prop.pay_to}
                                                onChange={(e) => {
                                                    const v = e.target.value
                                                    updatePropertyForm(
                                                        index,
                                                        getPropertyRowAfterPayToChange(index, v, company, companyForm)
                                                    )
                                                }}
                                                disabled={propertySidebarDisabled}
                                                className={cn(PAY_FIELD, "w-full")}
                                            >
                                                <option value="">Select Type</option>
                                                <option value="COMPANY">Company</option>
                                                <option value="PROPERTY">Property</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={PROP_LABEL}>Method</label>
                                            <select
                                                data-testid={`company-payments-property-${prop.property_id}-method-select`}
                                                value={
                                                    prop.pay_to === "COMPANY"
                                                        ? companyForm.payment_method || prop.payment_method
                                                        : prop.payment_method
                                                }
                                                onChange={(e) => {
                                                    const v = e.target.value
                                                    if (prop.pay_to === "COMPANY") {
                                                        updateCompanyForm({ payment_method: v })
                                                        updatePropertyForm(index, { payment_method: v })
                                                    } else {
                                                        updatePropertyForm(index, { payment_method: v })
                                                    }
                                                }}
                                                disabled={propertySidebarDisabled}
                                                className={cn(PAY_FIELD, "w-full")}
                                            >
                                                <option value="">Select Type</option>
                                                <option value="ACH">ACH</option>
                                                <option value="PAPER_CHECK">Paper</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={PROP_LABEL}>Verified</label>
                                            <select
                                                data-testid={`company-payments-property-${prop.property_id}-verified-select`}
                                                value={prop.verify}
                                                onChange={(e) => updatePropertyForm(index, { verify: e.target.value })}
                                                disabled={propertySidebarDisabled}
                                                className={cn(PAY_FIELD, "w-full")}
                                            >
                                                <option value="">Select Type</option>
                                                <option value="1">Yes</option>
                                                <option value="0">No</option>
                                            </select>
                                        </div>
                                    </aside>

                                    <div className="min-w-0 flex-1 space-y-0 border-t border-slate-200/80 bg-white p-4 sm:p-5 lg:border lg:border-l-0 lg:border-slate-200/80 lg:rounded-r-xl lg:py-6">
                                        {showPropertyAchRow && (
                                            <div className="space-y-5">
                                                <div className={PROP_INNER_CARD}>
                                                    <p className={PROP_SECTION_TITLE}>ACH account & documents</p>
                                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                                        <div className="min-w-0">
                                                            <label className={PROP_LABEL}>
                                                                ACH routing<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            data-testid={`company-payments-property-${prop.property_id}-ach-routing-input`}
                                                            maxLength={10}
                                                                placeholder="Routing number"
                                                            value={prop.routing_number}
                                                            onChange={(e) =>
                                                                    updatePropertyForm(index, {
                                                                        routing_number: e.target.value,
                                                                    })
                                                            }
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            className={cn(PAY_FIELD, "w-full")}
                                                        />
                                                    </div>
                                                        <div className="min-w-0">
                                                            <label className={PROP_LABEL}>
                                                                ACH account<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            data-testid={`company-payments-property-${prop.property_id}-ach-account-input`}
                                                            maxLength={13}
                                                                placeholder="Account number"
                                                            value={prop.account_number}
                                                            onChange={(e) =>
                                                                    updatePropertyForm(index, {
                                                                        account_number: e.target.value,
                                                                    })
                                                            }
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            className={cn(PAY_FIELD, "w-full")}
                                                        />
                                                    </div>
                                                        <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                                                            <label className={PROP_LABEL}>
                                                                Account type<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                        <select
                                                            data-testid={`company-payments-property-${prop.property_id}-ach-account-type-select`}
                                                            value={prop.property_method}
                                                            onChange={(e) =>
                                                                    updatePropertyForm(index, {
                                                                        property_method: e.target.value,
                                                                    })
                                                            }
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            className={cn(PAY_FIELD, "w-full")}
                                                        >
                                                                <option value="">Select type</option>
                                                                <option value="Checkings">Checking</option>
                                                            <option value="Savings">Savings</option>
                                                        </select>
                                                    </div>
                                                    </div>
                                                    <div className="mt-5 grid grid-cols-1 gap-5 border-t border-slate-200/60 pt-5 lg:grid-cols-2">
                                                        <div className="min-w-0 rounded-lg border border-slate-200/90 bg-white p-3 shadow-sm">
                                                            <div className="mb-1.5 flex items-center gap-1.5">
                                                                <span className={cn(PROP_LABEL, "!mb-0")}>
                                                                    ACH form<span className="font-semibold text-[#dc2626]">*</span>
                                                                </span>
                                                                <AchPaymentFormHelperTip infoButtonDataTestId={`company-payments-property-${prop.property_id}-ach-form-helper-info-button`} />
                                                            </div>
                                                            <PaymentFileUploadField
                                                                className="mt-2"
                                                                testIdPrefix={`company-payments-property-${prop.property_id}-ach-form-upload`}
                                                                aria-label={`Upload ACH form for ${prop.property_name}`}
                                                                disabled={propertyAchPaperDetailDisabled}
                                                                isUploading={uploading === `prop_${index}_ach_file`}
                                                                chosenDisplayName={
                                                                    filePickerHint[`prop_${index}_ach_file`] ?? ""
                                                                }
                                                                onFilesSelected={(files) => {
                                                                    const key = `prop_${index}_ach_file`
                                                                    const f = files?.[0]
                                                                    if (f) {
                                                                        setFilePickerHint((prev) => ({
                                                                            ...prev,
                                                                            [key]: f.name,
                                                                        }))
                                                                    }
                                                                    handleUploadFile("ach_file", files, index)
                                                                }}
                                                            />
                                                            <div className="mt-2 space-y-1">
                                                        {normalizePaymentFiles(pp?.["ach_file"]).map((f, fi) => (
                                                                    <div
                                                                        key={f?.id ?? fi}
                                                                        className="flex items-center gap-2 text-sm"
                                                                    >
                                                                <button
                                                                    type="button"
                                                                    data-testid={`company-payments-property-${prop.property_id}-ach-file-${String(f.id)}-download-button`}
                                                                    className={PAY_LINK}
                                                                    disabled={propertyAchPaperDetailDisabled}
                                                                    onClick={() =>
                                                                        handleDownloadFile(
                                                                            String(f.id),
                                                                            FILE_TYPES.ACH,
                                                                            "PROPERTY",
                                                                            String(prop.property_id)
                                                                        )
                                                                    }
                                                                >
                                                                    {f?.attachment_name}
                                                                </button>
                                                                {canEdit && !propertyAchPaperDetailDisabled && (
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`company-payments-property-${prop.property_id}-ach-file-${String(f.id)}-remove-button`}
                                                                        onClick={() =>
                                                                            setShowDeleteFileConfirm({
                                                                                fileId: String(f.id),
                                                                                type: "ach_file",
                                                                                propertyIndex: index,
                                                                            })
                                                                        }
                                                                        className="text-red-600"
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                        </div>
                                                        <div className="min-w-0 rounded-lg border border-slate-200/90 bg-white p-3 shadow-sm">
                                                            <label className={PROP_LABEL}>
                                                                Cancelled check<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                            <PaymentFileUploadField
                                                                className="mt-2"
                                                                testIdPrefix={`company-payments-property-${prop.property_id}-cancelled-check-upload`}
                                                                aria-label={`Upload cancelled check for ${prop.property_name}`}
                                                                disabled={propertyAchPaperDetailDisabled}
                                                                isUploading={
                                                                    uploading === `prop_${index}_cancelled_check`
                                                                }
                                                                chosenDisplayName={
                                                                    filePickerHint[`prop_${index}_cancelled_check`] ??
                                                                    ""
                                                                }
                                                                onFilesSelected={(files) => {
                                                                    const key = `prop_${index}_cancelled_check`
                                                                    const f = files?.[0]
                                                                    if (f) {
                                                                        setFilePickerHint((prev) => ({
                                                                            ...prev,
                                                                            [key]: f.name,
                                                                        }))
                                                                    }
                                                                    handleUploadFile(
                                                                        "cancelled_check",
                                                                        files,
                                                                        index
                                                                    )
                                                                }}
                                                            />
                                                            <div className="mt-2 space-y-1">
                                                                {normalizePaymentFiles(pp?.["cancelled_check"]).map(
                                                                    (f, fi) => (
                                                                        <div
                                                                            key={f?.id ?? fi}
                                                                            className="flex items-center gap-2 text-sm"
                                                                        >
                                                                <button
                                                                    type="button"
                                                                    data-testid={`company-payments-property-${prop.property_id}-cancelled-check-file-${String(f.id)}-download-button`}
                                                                    className={PAY_LINK}
                                                                    disabled={propertyAchPaperDetailDisabled}
                                                                    onClick={() =>
                                                                        handleDownloadFile(
                                                                            String(f.id),
                                                                            FILE_TYPES.CANCEL_CHECK,
                                                                            "PROPERTY",
                                                                            String(prop.property_id)
                                                                        )
                                                                    }
                                                                >
                                                                    {f?.attachment_name}
                                                                </button>
                                                                {canEdit && !propertyAchPaperDetailDisabled && (
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`company-payments-property-${prop.property_id}-cancelled-check-file-${String(f.id)}-remove-button`}
                                                                        onClick={() =>
                                                                            setShowDeleteFileConfirm({
                                                                                fileId: String(f.id),
                                                                                type: "cancelled_check",
                                                                                propertyIndex: index,
                                                                            })
                                                                        }
                                                                        className="text-red-600"
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                                    )
                                                                )}
                                                    </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-4 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                                                    <div className="flex min-w-0 flex-col gap-2 text-sm text-slate-500 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6">
                                                        <span>
                                                            Last update by{" "}
                                                        {companyForm.payment_method_to_property === "1"
                                                                ? String(company?.["payment_updated_by"] ?? "—")
                                                                : String(pp?.["payment_updated_by"] ?? "—")}{" "}
                                                        {companyForm.payment_method_to_property === "1"
                                                            ? formatPaymentUpdatedDate(company?.["payment_updated_date"])
                                                            : formatPaymentUpdatedDate(pp?.["payment_updated_date"])}
                                                    </span>
                                                    {canDefaultPaymentLogs && (
                                                        <button
                                                            type="button"
                                                            data-testid={`company-payments-property-${prop.property_id}-default-payment-history-link-ach`}
                                                            onClick={() =>
                                                                navigateToDefaultPaymentLogs(
                                                                    prop.property_id,
                                                                    String(prop.property_name ?? ''),
                                                                )
                                                            }
                                                            className={cn(
                                                                PAY_LINK,
                                                                "shrink-0 cursor-pointer border-0 bg-transparent p-0 text-left",
                                                                propertyPaymentsLocked && "pointer-events-auto",
                                                            )}
                                                        >
                                                            Default payment history
                                                        </button>
                                                    )}
                                                </div>
                                                {canEdit && !propertyPaymentsLocked && (
                                                    <button
                                                        type="button"
                                                        data-testid={`company-payments-property-${prop.property_id}-save-button-ach`}
                                                        onClick={() => setSavePropertyIndex(index)}
                                                        disabled={saving}
                                                            className={cn(PAY_BTN_PRIMARY, "w-full shrink-0 sm:w-auto")}
                                                    >
                                                        Save
                                                    </button>
                                                )}
                                                </div>
                                            </div>
                                        )}

                                        {showPropertyPaperRow && (
                                            <div className="space-y-5">
                                                <div className={PROP_INNER_CARD}>
                                                    <p className={PROP_SECTION_TITLE}>Paper check mailing address</p>
                                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12 lg:gap-x-4 lg:gap-y-4">
                                                        <div className="sm:col-span-2 lg:col-span-5">
                                                            <label className={PROP_LABEL}>
                                                                ATTN payee<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                        <select
                                                            data-testid={`company-payments-property-${prop.property_id}-paper-attn-payee-select`}
                                                            value={prop.payee_name}
                                                            onChange={(e) =>
                                                                updatePropertyForm(index, { payee_name: e.target.value })
                                                            }
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            className={cn(PAY_FIELD, "w-full")}
                                                        >
                                                                <option value="">Select ATTN contact</option>
                                                                <option value="LEASING">Leasing office</option>
                                                            {staffForPayee.map((s) => (
                                                                <option key={s.user_id} value={String(s.user_id)}>
                                                                    {s.first_name} {s.last_name}
                                                                </option>
                                                            ))}
                                                            <option value="OTHER">Other</option>
                                                        </select>
                                                    </div>
                                                    {prop.payee_name === "OTHER" && (
                                                            <div className="sm:col-span-2 lg:col-span-7">
                                                                <label className={PROP_LABEL}>Other payee</label>
                                                            <input
                                                                type="text"
                                                                data-testid={`company-payments-property-${prop.property_id}-paper-other-payee-input`}
                                                                value={prop.other_payee_name ?? ""}
                                                                onChange={(e) =>
                                                                    updatePropertyForm(index, {
                                                                        other_payee_name: e.target.value,
                                                                    })
                                                                }
                                                                disabled={propertyAchPaperDetailDisabled}
                                                                className={cn(PAY_FIELD, "w-full")}
                                                            />
                                                        </div>
                                                    )}
                                                        <div className="sm:col-span-2 lg:col-span-12">
                                                            <label className={PROP_LABEL}>
                                                                Street address<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                        <textarea
                                                                data-testid={`company-payments-property-${prop.property_id}-paper-street-address-textarea`}
                                                                rows={2}
                                                                placeholder="Street address"
                                                            value={prop.payment_address}
                                                            onChange={(e) => {
                                                                    updatePropertyForm(index, {
                                                                        payment_address: e.target.value,
                                                                    })
                                                                    clearPaymentAddrFieldError(
                                                                        "payment_address",
                                                                        "property",
                                                                        index
                                                                    )
                                                            }}
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            aria-invalid={
                                                                paymentAddrValidateErrors?.scope === "property" &&
                                                                paymentAddrValidateErrors.propertyIndex === index &&
                                                                !!paymentAddrValidateErrors.fields.payment_address
                                                            }
                                                                className={cn(
                                                                    PAY_FIELD,
                                                                    "min-h-[4.5rem] w-full resize-y",
                                                                    paymentAddrValidateErrors?.scope === "property" &&
                                                                        paymentAddrValidateErrors.propertyIndex ===
                                                                            index &&
                                                                        paymentAddrValidateErrors.fields
                                                                            .payment_address &&
                                                                        PAY_ADDR_INVALID_RING
                                                                )}
                                                        />
                                                            {paymentAddrValidateErrors?.scope === "property" &&
                                                            paymentAddrValidateErrors.propertyIndex === index &&
                                                            paymentAddrValidateErrors.fields.payment_address ? (
                                                                <PaymentAddrFieldError message="Street address is required" />
                                                            ) : null}
                                                    </div>
                                                        <div className="sm:col-span-2 lg:col-span-3">
                                                            <label className={PROP_LABEL}>Suite / mailstop</label>
                                                        <input
                                                            type="text"
                                                                data-testid={`company-payments-property-${prop.property_id}-paper-suite-mailstop-input`}
                                                                placeholder="Suite / mailstop"
                                                            value={prop.payment_shop}
                                                            onChange={(e) =>
                                                                updatePropertyForm(index, { payment_shop: e.target.value })
                                                            }
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            className={cn(PAY_FIELD, "w-full")}
                                                        />
                                                    </div>
                                                        <div className="sm:col-span-1 lg:col-span-3">
                                                            <label className={PROP_LABEL}>
                                                            City<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                                data-testid={`company-payments-property-${prop.property_id}-paper-city-input`}
                                                            placeholder="City"
                                                            value={prop.payment_city}
                                                            onChange={(e) => {
                                                                updatePropertyForm(index, {
                                                                    payment_city: e.target.value,
                                                                })
                                                                clearPaymentAddrFieldError(
                                                                    "payment_city",
                                                                    "property",
                                                                    index
                                                                )
                                                            }}
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            aria-invalid={
                                                                paymentAddrValidateErrors?.scope === "property" &&
                                                                paymentAddrValidateErrors.propertyIndex === index &&
                                                                !!paymentAddrValidateErrors.fields.payment_city
                                                            }
                                                            className={cn(
                                                                PAY_FIELD,
                                                                "w-full",
                                                                paymentAddrValidateErrors?.scope === "property" &&
                                                                    paymentAddrValidateErrors.propertyIndex === index &&
                                                                    paymentAddrValidateErrors.fields.payment_city &&
                                                                    PAY_ADDR_INVALID_RING
                                                            )}
                                                        />
                                                            {paymentAddrValidateErrors?.scope === "property" &&
                                                            paymentAddrValidateErrors.propertyIndex === index &&
                                                            paymentAddrValidateErrors.fields.payment_city ? (
                                                                <PaymentAddrFieldError message="City is required" />
                                                            ) : null}
                                                    </div>
                                                        <div className="sm:col-span-1 lg:col-span-3">
                                                            <label className={PROP_LABEL}>
                                                            State<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                        <select
                                                            data-testid={`company-payments-property-${prop.property_id}-paper-state-select`}
                                                            value={prop.payment_state}
                                                            onChange={(e) => {
                                                                    updatePropertyForm(index, {
                                                                        payment_state: e.target.value,
                                                                    })
                                                                    clearPaymentAddrFieldError(
                                                                        "payment_state",
                                                                        "property",
                                                                        index
                                                                    )
                                                            }}
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            aria-invalid={
                                                                paymentAddrValidateErrors?.scope === "property" &&
                                                                paymentAddrValidateErrors.propertyIndex === index &&
                                                                !!paymentAddrValidateErrors.fields.payment_state
                                                            }
                                                            className={cn(
                                                                PAY_FIELD,
                                                                "w-full",
                                                                paymentAddrValidateErrors?.scope === "property" &&
                                                                    paymentAddrValidateErrors.propertyIndex === index &&
                                                                    paymentAddrValidateErrors.fields.payment_state &&
                                                                    PAY_ADDR_INVALID_RING
                                                            )}
                                                        >
                                                                <option value="">Select state</option>
                                                            {states.map((s) => (
                                                                <option key={s.id} value={s.state_code}>
                                                                    {s.state_name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                            {paymentAddrValidateErrors?.scope === "property" &&
                                                            paymentAddrValidateErrors.propertyIndex === index &&
                                                            paymentAddrValidateErrors.fields.payment_state ? (
                                                                <PaymentAddrFieldError message="State is required" />
                                                            ) : null}
                                                    </div>
                                                        <div className="sm:col-span-2 lg:col-span-3">
                                                            <label className={PROP_LABEL}>
                                                                ZIP code<span className="font-semibold text-[#dc2626]">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                                data-testid={`company-payments-property-${prop.property_id}-paper-zip-input`}
                                                                placeholder="ZIP"
                                                            value={prop.payment_zipcode}
                                                            onChange={(e) => {
                                                                    updatePropertyForm(index, {
                                                                        payment_zipcode: e.target.value,
                                                                    })
                                                                    clearPaymentAddrFieldError(
                                                                        "payment_zipcode",
                                                                        "property",
                                                                        index
                                                                    )
                                                            }}
                                                            disabled={propertyAchPaperDetailDisabled}
                                                            aria-invalid={
                                                                paymentAddrValidateErrors?.scope === "property" &&
                                                                paymentAddrValidateErrors.propertyIndex === index &&
                                                                !!paymentAddrValidateErrors.fields.payment_zipcode
                                                            }
                                                            className={cn(
                                                                PAY_FIELD,
                                                                "w-full",
                                                                paymentAddrValidateErrors?.scope === "property" &&
                                                                    paymentAddrValidateErrors.propertyIndex === index &&
                                                                    paymentAddrValidateErrors.fields.payment_zipcode &&
                                                                    PAY_ADDR_INVALID_RING
                                                            )}
                                                        />
                                                            {paymentAddrValidateErrors?.scope === "property" &&
                                                            paymentAddrValidateErrors.propertyIndex === index &&
                                                            paymentAddrValidateErrors.fields.payment_zipcode ? (
                                                                <PaymentAddrFieldError message="ZIP code is required" />
                                                            ) : null}
                                                    </div>
                                                    </div>
                                                        {prop.payment_method === "PAPER_CHECK" &&
                                                            canEdit &&
                                                            !propertyPaymentsLocked && (
                                                        <div className="mt-5 flex flex-col gap-2 border-t border-slate-200/60 pt-4 sm:flex-row sm:items-center sm:justify-end">
                                                            <button
                                                                type="button"
                                                                data-testid={`company-payments-property-${prop.property_id}-paper-validate-address-button`}
                                                                onClick={() => handleValidatePaymentAddress(index)}
                                                                disabled={propertyAchPaperDetailDisabled}
                                                                className={cn(PAY_BTN_PRIMARY, "w-full sm:w-auto")}
                                                            >
                                                                Validate address
                                                            </button>
                                                        </div>
                                                        )}
                                                    </div>
                                                </div>
                                        )}

                                        {(showPropertyPaperRow ||
                                            (!showPropertyAchRow && !showPropertyPaperRow)) && (
                                            <div
                                                className={cn(
                                                    "flex flex-col gap-4 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between",
                                                    showPropertyPaperRow && "mt-5"
                                                )}
                                            >
                                                <div className="flex min-w-0 flex-col gap-4 text-sm sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-8">
                                                {showPropEmailBlock && (
                                                        <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:gap-8">
                                                            <div className="min-w-0 max-w-xs">
                                                                <p className={PROP_LABEL}>Email address</p>
                                                                <p className="mt-1 break-all text-sm text-slate-800">
                                                                    {propEmailDisplay || "—"}
                                                            </p>
                                                        </div>
                                                            <div className="min-w-0 max-w-xs">
                                                                <p className={PROP_LABEL}>Email verified</p>
                                                                <p className="mt-1 text-sm text-slate-800">
                                                                {propVerifiedDisplay}
                                                            </p>
                                                        </div>
                                                        {showEditPropPayeeUser ? (
                                                                <div className="flex items-end pb-0.5">
                                                                <a
                                                                    data-testid={`company-payments-property-${prop.property_id}-paper-edit-payee-user-link`}
                                                                    href={getUserManagementEditUrl(String(pp["payee_user_id"]))}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className={cn(
                                                                        PAY_BTN_PRIMARY,
                                                                        "inline-flex no-underline"
                                                                    )}
                                                                >
                                                                        Edit user
                                                                </a>
                                                            </div>
                                                        ) : null}
                                                        </div>
                                                )}
                                                    <div className="flex min-w-0 flex-col gap-2 text-slate-500 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6">
                                                        <span>
                                                            Last update by{" "}
                                                    {companyForm.payment_method_to_property === "1"
                                                                ? String(company?.["payment_updated_by"] ?? "—")
                                                                : String(pp?.["payment_updated_by"] ?? "—")}{" "}
                                                    {companyForm.payment_method_to_property === "1"
                                                        ? formatPaymentUpdatedDate(company?.["payment_updated_date"])
                                                        : formatPaymentUpdatedDate(pp?.["payment_updated_date"])}
                                                </span>
                                                {canDefaultPaymentLogs && (
                                                    <button
                                                        type="button"
                                                        data-testid={`company-payments-property-${prop.property_id}-default-payment-history-link-footer`}
                                                        onClick={() =>
                                                            navigateToDefaultPaymentLogs(
                                                                prop.property_id,
                                                                String(prop.property_name ?? ''),
                                                            )
                                                        }
                                                        className={cn(
                                                            PAY_LINK,
                                                            "shrink-0 cursor-pointer border-0 bg-transparent p-0 text-left",
                                                            propertyPaymentsLocked && "pointer-events-auto",
                                                        )}
                                                    >
                                                        Default payment history
                                                    </button>
                                                )}
                                                    </div>
                                            </div>
                                            {canEdit && !propertyPaymentsLocked && (
                                                <button
                                                    type="button"
                                                    data-testid={`company-payments-property-${prop.property_id}-save-button-footer`}
                                                    onClick={() => setSavePropertyIndex(index)}
                                                    disabled={saving}
                                                        className={cn(PAY_BTN_PRIMARY, "w-full shrink-0 sm:w-auto")}
                                                >
                                                    Save
                                                </button>
                                            )}
                                        </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                    </div>
                </div>
            </SectionCard>
            ) : null}
        </>
    )
}
